import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-15"
           style={{ backgroundImage: "url(https://images.pexels.com/photos/10341390/pexels-photo-10341390.jpeg?auto=compress&cs=tinysrgb&h=650&w=940)", backgroundSize: "cover", backgroundPosition: "center" }} />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#101010]/95 to-[#0a0a0a] pointer-events-none" />

      <div className="relative w-full max-w-md" data-testid="forgot-password-page">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.5em] text-[var(--org-accent)] mb-2">
            <span className="w-8 h-px bg-[var(--org-accent)]" /> Password Reset <span className="w-8 h-px bg-[var(--org-accent)]" />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-white uppercase tracking-tight leading-none">
            Forgot Password
          </h1>
          <div className="mt-3 text-xs uppercase tracking-widest text-[#a0a0a0]">
            {sent ? "Check your inbox" : "Enter your email to get a reset link"}
          </div>
        </div>

        {sent ? (
          <div className="border border-white/10 bg-[#1c1c1c]/90 backdrop-blur p-6 space-y-4" data-testid="forgot-password-sent">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-[var(--org-accent)] mt-0.5 flex-shrink-0" />
              <div className="text-sm text-[#e6e6e6] leading-relaxed">
                If an account exists for <span className="text-white font-semibold">{email}</span>, we've emailed a link to reset your password. The link expires in 1 hour.
              </div>
            </div>
            <div className="text-[11px] text-[#8a8a8a] leading-relaxed border-t border-white/10 pt-3">
              Didn't get the email? Check spam, or contact your admin — your address may not be registered.
            </div>
            <Link to="/login" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[var(--org-accent)] hover:text-white" data-testid="back-to-login">
              <ArrowLeft className="w-3 h-3" /> Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="border border-white/10 bg-[#1c1c1c]/90 backdrop-blur p-6 space-y-5">
            <div>
              <label className="text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0] mb-1.5 block">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-11 px-3 bg-[#101010] border border-white/10 text-white text-sm focus:outline-none focus:border-[var(--org-accent)]"
                required
                autoFocus
                autoComplete="email"
                data-testid="forgot-email"
              />
            </div>

            {error && (
              <div className="text-xs text-red-400 border border-red-400/30 bg-red-400/5 px-3 py-2" data-testid="forgot-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="w-full flex items-center justify-center gap-2 h-11 bg-[var(--org-accent)] text-[#121212] text-xs uppercase tracking-[0.3em] font-bold hover:bg-[var(--org-accent-hover)] disabled:opacity-60"
              style={{ transition: "background-color 0.15s ease" }}
              data-testid="forgot-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              Send reset link
            </button>

            <Link to="/login" className="flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#8a8a8a] hover:text-white" data-testid="back-to-login">
              <ArrowLeft className="w-3 h-3" /> Back to sign in
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
